import {filter, observeOn, map} from 'rxjs/operators';
import {Component, ViewChild, OnInit, AfterViewInit} from '@angular/core';
import {Client, EfsStatusType} from '../../../../services/api.client';
import {Efs} from '../../../../services/api.client';
import {BehaviorSubject, Observable, asapScheduler, combineLatestWith} from 'rxjs';
import {formatDate, normalize} from '../../../../utilities/Util';
import {UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, FormControl} from '@angular/forms';
import {LookUp} from '../../../../services/api.client';
import {ITableHeader} from '../../../shared/table/TableComponent';
import {StatusType} from '../../../../services/api.client';
import {subtractTime, nowDate, toDate, isSameOrAfter, startOf} from '../../../../utilities/date-util';
import {EfsService} from '../../../../services/EfsService';
import {PledgeType} from '../../../../services/api.client';
import {PledgeTypeService} from '../../../../services/PledgeTypeService';
import {UserType} from '../../../../services/api.client';
import {User} from '../../../../services/api.client';
import {SHORT_DATE_FORMAT} from '../../../../constants';
import * as _ from 'lodash';
import {Validators} from '../../../../utilities/Validators';
import {ClientEfsConfirmationComponent} from './ClientEfsConfirmationComponent';
import {ClientService} from '../../../../services/ClientService';
import {SecurityService} from '../../../../services/SecurityService';
import swal from 'sweetalert2';

@Component({
    selector: 'ClientEfsComponent',
    templateUrl: './ClientEfsComponent.html',
    styleUrls: ['./ClientEfsComponent.scss'],
    standalone: false
})

export class ClientEfsComponent implements OnInit, AfterViewInit {

    get id(): FormControl {
        return this.form?.get('id') as FormControl;
    }

    get clientId(): FormControl {
        return this.form?.get('clientId') as FormControl;
    }

    get agencyId(): FormControl {
        return this.form?.get('agencyId') as FormControl;
    }

    get agencyName(): FormControl {
        return this.form?.get('agencyName') as FormControl;
    }

    get assistanceType(): FormControl {
        return this.form?.get('assistanceType') as FormControl;
    }

    get pledgeTypeId(): FormControl {
        return this.form?.get('pledgeTypeId') as FormControl;
    }

    get zipCode(): FormControl {
        return this.form?.get('zipCode') as FormControl;
    }

    get description(): FormControl {
        return this.form?.get('description') as FormControl;
    }

    get confirmed(): FormControl {
        return this.form?.get('confirmed') as FormControl;
    }

    get status(): FormControl {
        return this.form?.get('status') as FormControl;
    }

    get assistanceDate(): FormControl {
        return this.form?.get('assistanceDate') as FormControl;
    }

    get enteredDate(): FormControl {
        return this.form?.get('enteredDate') as FormControl;
    }

    get modifiedDate(): FormControl {
        return this.form?.get('clientId') as FormControl;
    }

    get pledgeTypeName(): FormControl {
        return this.form?.get('pledgeTypeName') as FormControl;
    }

    constructor(private _efsService: EfsService,
                private _clientService: ClientService,
                private _pledgeTypeService: PledgeTypeService,
                private _formBuilder: UntypedFormBuilder,
                private _securityService: SecurityService) {
        this.loggedInUser$ = this._securityService.currentUser$;
        this.assistanceTypes$ = this._securityService.assistanceType$;
        // 003-retire-ngrx (US2): the client slice migrated to ClientService, and
        // the efs slice went component-local (list + selection live on this page
        // now). US3: pledgeType now reads from PledgeTypeService streams.
        this.currentClient$ = this._clientService.currentClient$;
        this.allPledgeTypes$ = this._pledgeTypeService.allPledgeTypes$;
    }

    existingAssistancerErrorMessage = '';
    isLoading: boolean;
    userTypes = UserType;
    selectedPledgeType: PledgeType;
    efsPledgeTypes: PledgeType[] = [];
    efsFilteredList: Efs[] = [];
    selectedRow: Efs;

    @ViewChild('confirmPrompt')
    confirmPrompt: ClientEfsConfirmationComponent;

    assistanceTypes: LookUp[];
    assistanceTypes$: Observable<LookUp[]>;

    currentClient: Client;
    currentClient$: Observable<Client>;

    currentClientEfsList: Efs[];

    // 003-retire-ngrx (US2): the current selection is component-local now — a
    // BehaviorSubject keeps the existing reactive form pipelines intact.
    private selectedEfsSubject = new BehaviorSubject<Efs>(null);
    selectedEfs: Efs;
    selectedEfs$: Observable<Efs> = this.selectedEfsSubject.asObservable();

    allPledgeTypes: PledgeType[];
    allPledgeTypes$: Observable<PledgeType[]>;

    loggedInUser: User;
    loggedInUser$: Observable<User>;

    form: UntypedFormGroup;

    tableHeaders: ITableHeader[] = [
        {text: 'EFS Cycle', sortKey: 'pledgeTypeName'},
        {text: 'Assistance Type', sortKey: 'assistanceType'},
        {text: 'Date', sortKey: 'assistanceDate'},
        {text: 'Agency Name', sortKey: 'AgencyName', class: 'hidden-sm-down'}
    ];

    // sorting
    currentSortName = 'Confirmed';
    currentSorting = false;

    ngOnInit(): void {
        this._pledgeTypeService.getAllPledgeTypes();
        this.loggedInUser$.subscribe(user => this.loggedInUser = user);
        this.assistanceTypes$.subscribe(assistanceTypes => this.assistanceTypes = assistanceTypes);
        this.currentClient$.subscribe(currentClient => this.currentClient = currentClient);
        this.selectedEfs$.subscribe(selectedEfs => this.selectedEfs = selectedEfs);
        this.allPledgeTypes$.subscribe(allPledgeTypes => this.allPledgeTypes = allPledgeTypes);
        this.currentClient$.pipe(
            filter(client => !!client))
            .subscribe(client => {
                this.currentClient = client;
                this.filterEfs();
            });

        this.allPledgeTypes$.pipe(
            filter(allPledgeTypes => !!allPledgeTypes),
            map((pledgeTypes => pledgeTypes.filter(p => p.isEfs))), )
            .subscribe((allPledgeTypes) => {
                this.efsPledgeTypes = [];
                if (this.loggedInUser && this.loggedInUser.userType === UserType.SystemAdmin) {
                    for (const pledgeType of allPledgeTypes) {
                        if (isSameOrAfter(pledgeType.efsEndDate, nowDate())) {
                            this.efsPledgeTypes.push(pledgeType);
                        }
                    }
                } else {
                    for (const pledgeType of allPledgeTypes) {
                        if (this.loggedInUser.agency) {
                            if (this.loggedInUser.agency.pledgeTypeIds
                                .some(x => x === pledgeType.id && isSameOrAfter(pledgeType.efsEndDate, nowDate()))) {
                                this.efsPledgeTypes.push(pledgeType);
                            }
                        }
                    }
                }

                if (this.efsPledgeTypes.length === 1) {
                    if (this.pledgeTypeId && this.pledgeTypeName) {
                        this.pledgeTypeId.setValue(this.efsPledgeTypes[0].id);
                        this.pledgeTypeName.setValue(this.efsPledgeTypes[0].name);
                    }
                }
            });

        this.setClientName();
        this.initForm();
        this.filterEfs();

        // disable filds based on current user
        if (this.loggedInUser) {
            if (this.loggedInUser.userType === this.userTypes.Viewer) {
                this.form.controls.pledgeTypeId.disable();
                this.form.controls.assistanceType.disable();
                this.form.controls.assistanceDate.disable();
                // .setValue(({value: null, disabled: 'disable'}, { onlySelf: true }));
            } else {
                this.form.controls.pledgeTypeId.enable();
                this.form.controls.assistanceType.enable();
                this.form.controls.assistanceDate.enable();
            }
        }
    }

    // set client name
    setClientName(): string {
        if (this.currentClient) {
            return this.currentClient.firstName + ' ' + this.currentClient.lastName;
        }
        return '';
    }

    // set client zip code
    setClientZipCode(): string {
        if (this.currentClient) {
            return this.currentClient.zipCode;
        }
        return '';
    }

    initForm(): void {
        this.form = this._formBuilder.group({
            id: new FormControl(null),
            clientId: new FormControl(this.currentClient.id, Validators.required),
            agencyId: new FormControl(this.loggedInUser.agencyId ? this.loggedInUser.agencyId : null),
            agencyName: new FormControl(null),
            assistanceType: new FormControl(null, Validators.compose([Validators.required, duplicateAssistanceTypeValidator.bind(this)])),
            pledgeTypeId: new FormControl(null, Validators.compose([Validators.required])),
            pledgeTypeName: new FormControl(null, Validators.required),
            zipCode: new FormControl(this.currentClient.zipCode, Validators.required),
            description: new FormControl(null),
            confirmed: new FormControl(EfsStatusType.Confirmed),
            status: new FormControl(StatusType.Enabled, Validators.required),
            enteredDate: new FormControl(formatDate(nowDate(), SHORT_DATE_FORMAT)),
            modifiedDate: new FormControl(formatDate(nowDate(), SHORT_DATE_FORMAT)),
            assistanceDate: new FormControl({value: startOf(nowDate(), 'day'), disabled: false},
                Validators.compose([Validators.required, Validators.isDateValid, Validators.dateRange(subtractTime(nowDate(), 60, 'days'), nowDate())]))
        });

        this.selectedEfs$
            .modelExists()
            .subscribe((efs) => {
                const fields = Object.assign({}, efs, {});
                if (fields.assistanceDate) {
                    (fields as any).assistanceDate = toDate(fields.assistanceDate);
                }

                if (fields.enteredDate) {
                    (fields as any).enteredDate = formatDate(fields.enteredDate, 'MM/DD/YYYY HH:mm a');
                }

                if (fields.modifiedDate) {
                    (fields as any).modifiedDate = formatDate(fields.modifiedDate, 'L');
                }
                this.pledgeTypeName.setValue(fields.pledgeTypeName);
                this.form.patchValue(fields);
            });

        this.assistanceDate.valueChanges
            .subscribe(() => {
                this.form.markAsDirty();
            });

        this.pledgeTypeId.valueChanges.pipe(
            combineLatestWith(this.allPledgeTypes$.exists()))
            .subscribe(([id, pledgeTypes]) => {
                this.pledgeTypeName.setValue(normalize(id) ? pledgeTypes.find((efs) => efs.id === id).name : null);
            });

        this.pledgeTypeId.valueChanges.pipe(
            combineLatestWith(this.selectedEfs$, this.pledgeTypeId.valueChanges),
            observeOn(asapScheduler))
            .subscribe(() => this.assistanceType.updateValueAndValidity());

        function duplicateAssistanceTypeValidator(): any {
            if (!this.assistanceType) {
                return;
            }
            const assistanceType = this.assistanceType.value;
            const pledgeTypeId = this.pledgeTypeId.value;
            if (assistanceType) {
                const efs: Efs = _.find<Efs>(this.currentClientEfsList, {
                    assistanceType: assistanceType as any,
                    pledgeTypeId: pledgeTypeId as any
                } as any);
                if (this.selectedEfs) {// edit EFS
                    if (efs && efs.id !== this.selectedEfs.id) {
                        return {duplicateAssistanceType: '"' + assistanceType + '"' + ' assistance type already exists for the selected  EFS cycle'};
                    } else {
                        return null;
                    }
                } else {// add new EFS
                    if (efs) {
                        return {duplicateAssistanceType: '"' + assistanceType + '"' + ' assistance type already exists for the selected  EFS cycle'};
                    } else {
                        return null;
                    }
                }
            }
        }
    }

    sortItems(options: ITableHeader): void {
        this.currentSortName = options.sortKey;
        this.currentSorting = !options.ascending;
        this.filterEfs();
    }

    // set Current Selected Assistance
    setClickedRow(selectedEfs: Efs): void {
        if (this.selectedRow === selectedEfs) {
            this.selectedRow = null;
            this.setSelectedEfs(null);
            this.resetScreenValues();
        } else {
            this.selectedRow = selectedEfs;
            this.setSelectedEfs(selectedEfs);
        }
    }

    // 003-retire-ngrx (US2): selection updates go through the local subject
    // instead of the store (the service no longer dispatches).
    private setSelectedEfs(efs: Efs): void {
        this.selectedEfsSubject.next(efs);
    }

    setMarkAsTouched(): void {
        this.form.markAsTouched();
    }

    filterByEfsCycle(pledgeTypeId): void {
        this.selectedPledgeType = (_.find(this.allPledgeTypes, {id: pledgeTypeId}) as any);
        this.efsFilteredList = pledgeTypeId !== ''
            ? _.filter(this.currentClientEfsList, {pledgeTypeId}) as any
            : this.currentClientEfsList;
        this.resetScreenValues();
    }

    filterEfs(): void {
        // 003-retire-ngrx (US2): the efs list is component-local now — same
        // fetch timing (page load / after add-update-delete), local render.
        this._efsService.getCurrentClientEfsList(this.currentClient.id, '')
            .subscribe({
                next: (currentClientEfsList) => {
                    this.currentClientEfsList = currentClientEfsList;
                    this.efsFilteredList = this.selectedPledgeType
                        ? _.filter(this.currentClientEfsList, {pledgeTypeId: this.selectedPledgeType.id})
                        : this.currentClientEfsList;
                },
                error: () => { /* the service already shows the error toast */ }
            });
        if (this.selectedPledgeType) {
            this.filterByEfsCycle(this.selectedPledgeType.id);
        }
    }

    isSystemAdmin(): boolean {
        if (!this.loggedInUser) {
            return false;
        }
        return this.loggedInUser.userType === UserType.SystemAdmin;
    }


    showConfirmPrompt(): void {
        this.confirmPrompt.isReprint = !this.form.dirty && this.selectedEfs && !!this.selectedEfs.id;

        this.confirmPrompt
            .open().pipe(
            filter(confirmed => confirmed))
            .subscribe(() => this.submit());
    }

    isClientValid(): boolean {
        if (this.currentClient) {
            return !!this.currentClient.county;
        }
    }

    // format Agency Name
    formatAgencyName(agencyId: string): string {
        if (this.currentClientEfsList) {
            for (const efs of this.currentClientEfsList) {
                if (efs.agencyId === agencyId) {
                    return efs.agencyName;
                }
            }
        }
        return 'missing agency';
    }

    submit(): void {
        const efs = this.form.value;

        if (!this.form.valid) {
            this.form.markAsTouched();
            return;
        } else {
            if (this.selectedEfs) {
                this._efsService.updateEfs(efs)
                    .subscribe((data) => {
                        // 003-retire-ngrx (US2): the service used to dispatch the
                        // saved efs to the store; select it locally instead.
                        this.setSelectedEfs(data);
                        this.pledgeTypeName.setValue(efs.pledgeTypeName);
                        this.filterEfs();
                        this.selectedRow = data;
                        this.showConfirmPrompt();
                        this.form.markAsUntouched();
                    });
            } else {
                const addEfs = Efs.fromJS({
                    clientId: efs.clientId,
                    agencyId: efs.agencyId,
                    agencyName: efs.agencyName,
                    assistanceType: efs.assistanceType,
                    pledgeTypeId: efs.pledgeTypeId,
                    pledgeTypeName: efs.pledgeTypeName,
                    zipCode: efs.zipCode,
                    description: efs.description,
                    confirmed: efs.confirmed,
                    status: efs.status,
                    enteredDate: efs.enteredDate,
                    modifiedDate: efs.modifiedDate,
                    assistanceDate: efs.assistanceDate
                });
                this._efsService.addEfs(addEfs)
                    .subscribe((data) => {
                        // 003-retire-ngrx (US2): the service used to dispatch the
                        // saved efs to the store; select it locally instead.
                        this.setSelectedEfs(data);
                        this.pledgeTypeName.setValue(efs.pledgeTypeName);
                        this.filterEfs();
                        this.selectedRow = data;
                        this.showConfirmPrompt();
                        this.form.markAsUntouched();
                    });
            }
        }
    }

    delete(): void {
        swal.fire({
            title: 'Delete EFS',
            text: 'Are you sure you want to delete this EFS entry?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            confirmButtonColor: '#d33',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                if (this.selectedEfs) {
                    this._efsService.deleteEfs(this.selectedEfs.id)
                        .subscribe(() => {
                            this.setSelectedEfs(null);
                            this.filterEfs();
                            this.resetScreenValues();
                        });
                }
            }
        });

    }

    Cancel(): void {
        this.resetScreenValues();
    }

    resetScreenValues(): void {
        this.setSelectedEfs(null);
        this.selectedRow = void 0;
        if (this.efsPledgeTypes.length !== 1) {
            this.pledgeTypeId.setValue(null);
        }
        this.assistanceType.setValue(null);
        this.id.setValue(null);
        this.assistanceDate.setValue(startOf(nowDate(), 'day'));
        this.form.markAsUntouched();
    }

    ngOnDestroy(): void {
        // 003-retire-ngrx (US2): selection is component-local, so it dies with
        // the component — clear it anyway to release late subscribers.
        this.setSelectedEfs(null);
    }

    ngAfterViewInit(): void {
    }
}
